import { createSelector } from '@reduxjs/toolkit'

import { servicesSlice } from '@/store/servicesSlice'
import { sumCartGroupTicketQuantity } from '@/utils/cartTicketQuantities'
import { hasConfiguredMaximum } from '@/utils/getReservationConstraintMessage'
import { ReservationValidationError } from '@/utils/reservationValidationMessages'

import { cartSlice } from '..'

export const cartReservationErrorsSelector = createSelector(
  [cartSlice.selectors.cartReservations, servicesSlice.selectors.data],
  (cartReservations, servicesData): ReservationValidationError[] => {
    if (!servicesData || cartReservations.length === 0) {
      return []
    }

    const errors: ReservationValidationError[] = []
    const ticketServiceIds = new Set(
      servicesData.ticketServices.map((service) => service.serviceId)
    )

    const globalTotal = sumCartGroupTicketQuantity({
      cartReservations,
      groupServiceIds: [...ticketServiceIds],
    })

    servicesData.serviceGroups.forEach((group) => {
      const groupServiceIds = group.services.map((service) => service.serviceId)
      const groupTotal = sumCartGroupTicketQuantity({
        cartReservations,
        groupServiceIds,
      })

      if (
        group.minimumReservationQuantity > 0 &&
        groupTotal < group.minimumReservationQuantity
      ) {
        errors.push({
          groupId: group.serviceGroupId,
          groupName: group.name,
          minQty: group.minimumReservationQuantity,
          type: 'groupMinQty',
        })
      }

      if (
        hasConfiguredMaximum(group.maximumReservationQuantity) &&
        groupTotal > group.maximumReservationQuantity
      ) {
        errors.push({
          groupId: group.serviceGroupId,
          groupName: group.name,
          maxQty: group.maximumReservationQuantity,
          type: 'groupMaxQty',
        })
      }
    })

    if (
      servicesData.reservationMinimumQuantity > 0 &&
      globalTotal < servicesData.reservationMinimumQuantity
    ) {
      errors.push({
        minQty: servicesData.reservationMinimumQuantity,
        type: 'globalMinQty',
      })
    }

    if (
      hasConfiguredMaximum(servicesData.reservationLimitQuantity) &&
      globalTotal > servicesData.reservationLimitQuantity
    ) {
      errors.push({
        maxQty: servicesData.reservationLimitQuantity,
        type: 'globalMaxQty',
      })
    }

    return errors
  }
)
